import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Progress } from "../../components/ui/progress";
import { Switch } from "../../components/ui/switch";
import { 
  Upload, 
  Play, 
  Pause, 
  Trash2, 
  RefreshCw, 
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  Zap,
  ExternalLink,
  FileJson,
  Activity,
  BarChart3,
  Settings2,
  Shield,
  Download,
  Eye,
  TestTube,
  Workflow,
  Bot,
  RotateCcw,
  AlertTriangle,
  ShieldAlert,
  Flag,
  History,
  HeartPulse
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_API_URL;

export default function AdminExternalAgents() {
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [usageStats, setUsageStats] = useState(null);
  const [rateLimits, setRateLimits] = useState(null);
  const [featureFlags, setFeatureFlags] = useState(null);
  const [healthDashboard, setHealthDashboard] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showVersionsDialog, setShowVersionsDialog] = useState(false);
  const [executions, setExecutions] = useState([]);
  const [versions, setVersions] = useState([]);
  const [circuitEvents, setCircuitEvents] = useState(null);
  const [testPayload, setTestPayload] = useState("{}");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDryRun, setIsDryRun] = useState(true);
  const [activeTab, setActiveTab] = useState("agents");
  const fileInputRef = useRef(null);

  const businessId = "speakr-admin";

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [agentsRes, usageRes, rateLimitsRes, flagsRes, healthRes] = await Promise.all([
        fetch(`${API_URL}/api/v2/external-agents/?business_id=${businessId}`),
        fetch(`${API_URL}/api/v2/external-agents/usage/${businessId}`),
        fetch(`${API_URL}/api/v2/external-agents/rate-limits/${businessId}`),
        fetch(`${API_URL}/api/v2/external-agents/feature-flags/${businessId}`),
        fetch(`${API_URL}/api/v2/external-agents/health-dashboard/${businessId}`)
      ]);

      if (agentsRes.ok) {
        const data = await agentsRes.json();
        setAgents(data.agents || []);
      }
      if (usageRes.ok) {
        setUsageStats(await usageRes.json());
      }
      if (rateLimitsRes.ok) {
        setRateLimits(await rateLimitsRes.json());
      }
      if (flagsRes.ok) {
        setFeatureFlags(await flagsRes.json());
      }
      if (healthRes.ok) {
        setHealthDashboard(await healthRes.json());
      }
    } catch (error) {
      console.error("Failed to load external agents:", error);
      toast.error("Failed to load external agents");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(
        `${API_URL}/api/v2/external-agents/upload?business_id=${businessId}`,
        { method: "POST", body: formData }
      );

      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Agent "${data.agent.agent_name}" registered successfully`);
        setShowUploadDialog(false);
        loadData();
      } else {
        toast.error(data.detail || "Failed to upload manifest");
      }
    } catch (error) {
      toast.error("Failed to upload manifest");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const activateAgent = async (recordId) => {
    try {
      const res = await fetch(`${API_URL}/api/v2/external-agents/${recordId}/activate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({})
      });

      if (res.ok) {
        toast.success("Agent activated");
        loadData();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to activate agent");
      }
    } catch (error) {
      toast.error("Failed to activate agent");
    }
  };

  const deactivateAgent = async (recordId) => {
    try {
      const res = await fetch(`${API_URL}/api/v2/external-agents/${recordId}/deactivate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({})
      });

      if (res.ok) {
        toast.success("Agent deactivated");
        loadData();
      } else {
        toast.error("Failed to deactivate agent");
      }
    } catch (error) {
      toast.error("Failed to deactivate agent");
    }
  };

  const deleteAgent = async (recordId) => {
    if (!confirm("Are you sure you want to delete this agent?")) return;

    try {
      const res = await fetch(`${API_URL}/api/v2/external-agents/${recordId}`, {
        method: "DELETE"
      });

      if (res.ok) {
        toast.success("Agent deleted");
        loadData();
      } else {
        toast.error("Failed to delete agent");
      }
    } catch (error) {
      toast.error("Failed to delete agent");
    }
  };

  const resetCircuitBreaker = async (recordId) => {
    try {
      const res = await fetch(`${API_URL}/api/v2/external-agents/${recordId}/reset-circuit-breaker`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });

      if (res.ok) {
        toast.success("Circuit breaker reset successfully");
        loadData();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to reset circuit breaker");
      }
    } catch (error) {
      toast.error("Failed to reset circuit breaker");
    }
  };

  const openTestDialog = async (agent) => {
    setSelectedAgent(agent);
    setTestPayload(JSON.stringify({
      call_id: "test-call-001",
      lead_id: "test-lead-001",
      company_name: "Acme Corp",
      email: "test@example.com"
    }, null, 2));
    setTestResult(null);
    setIsDryRun(true);
    setShowTestDialog(true);
  };

  const runTest = async () => {
    if (!selectedAgent) return;

    setTesting(true);
    setTestResult(null);

    try {
      let payload;
      try {
        payload = JSON.parse(testPayload);
      } catch {
        toast.error("Invalid JSON payload");
        setTesting(false);
        return;
      }

      const endpoint = isDryRun 
        ? `${API_URL}/api/v2/external-agents/${selectedAgent.id}/dry-run`
        : `${API_URL}/api/v2/external-agents/${selectedAgent.id}/test`;

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          trigger_event: "custom",
          payload,
          validate_capabilities: true
        })
      });

      const data = await res.json();
      setTestResult(data);

      if (data.status === "success") {
        toast.success(`${isDryRun ? "Dry-run" : "Test"} completed successfully`);
      } else {
        toast.error(`${isDryRun ? "Dry-run" : "Test"} failed: ${data.error_message || data.status}`);
      }
    } catch (error) {
      toast.error("Failed to run test");
    } finally {
      setTesting(false);
    }
  };

  const viewAgentDetails = async (agent) => {
    setSelectedAgent(agent);
    setShowDetailDialog(true);

    // Load executions and circuit events
    try {
      const [execRes, circuitRes] = await Promise.all([
        fetch(`${API_URL}/api/v2/external-agents/${agent.id}/executions?limit=20`),
        fetch(`${API_URL}/api/v2/external-agents/${agent.id}/circuit-breaker-events`)
      ]);

      if (execRes.ok) {
        const data = await execRes.json();
        setExecutions(data.executions || []);
      }
      if (circuitRes.ok) {
        setCircuitEvents(await circuitRes.json());
      }
    } catch (error) {
      console.error("Failed to load agent details:", error);
    }
  };

  const viewVersions = async (agent) => {
    setSelectedAgent(agent);
    setShowVersionsDialog(true);

    try {
      const res = await fetch(`${API_URL}/api/v2/external-agents/${agent.id}/versions`);
      if (res.ok) {
        const data = await res.json();
        setVersions(data.versions || []);
      }
    } catch (error) {
      console.error("Failed to load versions:", error);
    }
  };

  const rollbackVersion = async (versionId) => {
    if (!selectedAgent) return;
    
    try {
      const targetVersion = versions.find(v => v.id === versionId);
      if (!targetVersion) return;

      const res = await fetch(`${API_URL}/api/v2/external-agents/${selectedAgent.id}/rollback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target_version: targetVersion.version })
      });

      if (res.ok) {
        toast.success(`Rolled back to version ${targetVersion.version}`);
        setShowVersionsDialog(false);
        loadData();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to rollback");
      }
    } catch (error) {
      toast.error("Failed to rollback version");
    }
  };

  const updateFeatureFlag = async (flag, value) => {
    try {
      const res = await fetch(`${API_URL}/api/v2/external-agents/feature-flags/${businessId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ flags: { [flag]: value } })
      });

      if (res.ok) {
        setFeatureFlags(prev => ({ ...prev, [flag]: value }));
        toast.success(`Feature flag updated`);
      } else {
        toast.error("Failed to update feature flag");
      }
    } catch (error) {
      toast.error("Failed to update feature flag");
    }
  };

  const exportLogs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/v2/external-agents/export/${businessId}?mask_pii=true`);
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `external-agents-export-${new Date().toISOString().split("T")[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${data.record_count} records`);
      }
    } catch (error) {
      toast.error("Failed to export logs");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Active</Badge>;
      case "inactive":
        return <Badge variant="secondary"><Pause className="h-3 w-3 mr-1" />Inactive</Badge>;
      case "error":
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Error</Badge>;
      case "circuit_open":
        return <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />Circuit Open</Badge>;
      case "disabled":
        return <Badge variant="outline"><Trash2 className="h-3 w-3 mr-1" />Disabled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getProviderIcon = (provider) => {
    switch (provider) {
      case "n8n":
        return <Workflow className="h-4 w-4 text-orange-500" />;
      case "google_vertex":
        return <Bot className="h-4 w-4 text-blue-500" />;
      default:
        return <Zap className="h-4 w-4 text-purple-500" />;
    }
  };

  const getProviderLabel = (provider) => {
    switch (provider) {
      case "n8n": return "n8n Workflow";
      case "google_vertex": return "Google Vertex AI";
      case "custom_webhook": return "Custom Webhook";
      default: return provider;
    }
  };

  const getCapabilityBadge = (cap) => {
    const dangerous = ["requires_pii", "writes_lead_data", "triggers_notifications", "writes_crm"];
    const isDangerous = dangerous.includes(cap);
    return (
      <Badge 
        key={cap} 
        variant={isDangerous ? "destructive" : "secondary"}
        className={isDangerous ? "bg-amber-100 text-amber-800 border-amber-200" : ""}
      >
        {isDangerous && <ShieldAlert className="h-3 w-3 mr-1" />}
        {cap}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="external-agents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">External Agents</h1>
          <p className="text-gray-500 mt-1">Integrate external AI agents via webhook (n8n, Google Vertex, custom)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportLogs}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowUploadDialog(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Manifest
          </Button>
        </div>
      </div>

      {/* Rate Limit Warnings */}
      {rateLimits?.warnings?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-4">
            {rateLimits.warnings.map((warning, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <AlertTriangle className={`h-5 w-5 mt-0.5 ${warning.severity === "critical" ? "text-red-600" : "text-amber-600"}`} />
                <div>
                  <h4 className="font-medium text-amber-800">Rate Limit Warning</h4>
                  <p className="text-sm text-amber-700 mt-1">{warning.message}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="agents">Agents</TabsTrigger>
          <TabsTrigger value="health">Health Dashboard</TabsTrigger>
          <TabsTrigger value="settings">Feature Flags</TabsTrigger>
          <TabsTrigger value="usage">Usage & Limits</TabsTrigger>
        </TabsList>

        {/* Agents Tab */}
        <TabsContent value="agents">
          {/* Important Warning */}
          <Card className="border-amber-200 bg-amber-50 mb-4">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-800">External Agents Are Non-Voice</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    External agents process data in the background. They never speak to customers directly.
                    All voice interactions are handled by Speakr native agents.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Agents List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-indigo-600" />
                Registered Agents
              </CardTitle>
              <CardDescription>
                External AI agents integrated via webhook
              </CardDescription>
            </CardHeader>
            <CardContent>
              {agents.length === 0 ? (
                <div className="text-center py-12">
                  <FileJson className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">No External Agents</h3>
                  <p className="text-gray-500 mt-1 max-w-md mx-auto">
                    Upload an agent.manifest.json to integrate external AI agents like n8n workflows or Google Vertex AI.
                  </p>
                  <Button className="mt-4" onClick={() => setShowUploadDialog(true)}>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload First Agent
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {agents.map((agent) => (
                    <div 
                      key={agent.id}
                      className={`flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 ${
                        agent.status === "circuit_open" ? "border-red-300 bg-red-50" : ""
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-lg ${agent.status === "circuit_open" ? "bg-red-100" : "bg-gray-100"}`}>
                          {getProviderIcon(agent.provider)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium">{agent.agent_name}</h4>
                            <Badge variant="outline" className="text-xs">v{agent.version}</Badge>
                            {getStatusBadge(agent.status)}
                            {agent.is_active_version && (
                              <Badge className="bg-indigo-100 text-indigo-700 text-xs">Active Version</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{agent.description || "No description"}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                            <span className="flex items-center gap-1">
                              {getProviderIcon(agent.provider)}
                              {getProviderLabel(agent.provider)}
                            </span>
                            <span>Type: {agent.agent_type}</span>
                            <span>Triggers: {agent.trigger_events?.join(", ")}</span>
                          </div>
                          {/* Capability warnings */}
                          {agent.capabilities?.some(c => ["requires_pii", "writes_lead_data"].includes(c)) && (
                            <div className="flex gap-1 mt-2">
                              {agent.capabilities.filter(c => ["requires_pii", "writes_lead_data", "triggers_notifications"].includes(c)).map(getCapabilityBadge)}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {/* Health Metrics */}
                        <div className="text-right text-sm">
                          <div className="text-gray-500">
                            {agent.health?.total_executions || 0} runs
                          </div>
                          <div className={agent.health?.success_rate >= 90 ? "text-green-600" : agent.health?.success_rate >= 70 ? "text-amber-600" : "text-red-600"}>
                            {agent.health?.success_rate?.toFixed(1) || 0}% success
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex gap-2">
                          {agent.status === "circuit_open" && (
                            <Button variant="outline" size="sm" onClick={() => resetCircuitBreaker(agent.id)} className="text-amber-600 border-amber-300">
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={() => openTestDialog(agent)}>
                            <TestTube className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => viewAgentDetails(agent)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => viewVersions(agent)}>
                            <History className="h-4 w-4" />
                          </Button>
                          {agent.status === "active" || agent.is_active_version ? (
                            <Button variant="outline" size="sm" onClick={() => deactivateAgent(agent.id)}>
                              <Pause className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button variant="outline" size="sm" onClick={() => activateAgent(agent.id)}>
                              <Play className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={() => deleteAgent(agent.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Health Dashboard Tab */}
        <TabsContent value="health">
          {healthDashboard && (
            <div className="space-y-4">
              {/* Health Score */}
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Health Score</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <span className={`text-3xl font-bold ${
                      healthDashboard.summary.health_score >= 80 ? "text-green-600" :
                      healthDashboard.summary.health_score >= 50 ? "text-amber-600" : "text-red-600"
                    }`}>
                      {healthDashboard.summary.health_score}%
                    </span>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Active Agents</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <span className="text-3xl font-bold text-green-600">{healthDashboard.summary.active_agents}</span>
                    <span className="text-gray-500"> / {healthDashboard.summary.total_agents}</span>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Circuit Open</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <span className={`text-3xl font-bold ${healthDashboard.summary.circuit_open_agents > 0 ? "text-red-600" : "text-green-600"}`}>
                      {healthDashboard.summary.circuit_open_agents}
                    </span>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Errors</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <span className={`text-3xl font-bold ${healthDashboard.summary.error_agents > 0 ? "text-red-600" : "text-green-600"}`}>
                      {healthDashboard.summary.error_agents}
                    </span>
                  </CardContent>
                </Card>
              </div>

              {/* Agents with Issues */}
              {healthDashboard.agents_with_issues?.length > 0 && (
                <Card className="border-red-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="h-5 w-5" />
                      Agents Requiring Attention
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {healthDashboard.agents_with_issues.map((agent) => (
                        <div key={agent.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{agent.agent_name}</span>
                              {getStatusBadge(agent.status)}
                            </div>
                            {agent.last_error && (
                              <p className="text-sm text-red-600 mt-1 truncate max-w-lg">{agent.last_error}</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <span className="text-sm text-gray-500">{agent.failure_count} failures</span>
                            {agent.status === "circuit_open" && (
                              <Button size="sm" variant="outline" onClick={() => resetCircuitBreaker(agent.id)}>
                                <RotateCcw className="h-4 w-4 mr-1" />
                                Reset
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Failures */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Failures</CardTitle>
                </CardHeader>
                <CardContent>
                  {healthDashboard.recent_failures?.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="h-8 w-8 mx-auto text-green-500 mb-2" />
                      No recent failures
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {healthDashboard.recent_failures?.map((f) => (
                        <div key={f.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium">{f.agent_name}</p>
                            <p className="text-sm text-red-600 truncate max-w-md">{f.error_message}</p>
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(f.timestamp).toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Feature Flags Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Flag className="h-5 w-5" />
                Feature Flags
              </CardTitle>
              <CardDescription>
                Enable or disable features without redeploying
              </CardDescription>
            </CardHeader>
            <CardContent>
              {featureFlags && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium">External Agents</h4>
                      <p className="text-sm text-gray-500">Enable external agent integrations</p>
                    </div>
                    <Switch 
                      checked={featureFlags.external_agents_enabled}
                      onCheckedChange={(v) => updateFeatureFlag("external_agents_enabled", v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium">MAS Handoffs</h4>
                      <p className="text-sm text-gray-500">Enable multi-agent system handoffs</p>
                    </div>
                    <Switch 
                      checked={featureFlags.mas_handoffs_enabled}
                      onCheckedChange={(v) => updateFeatureFlag("mas_handoffs_enabled", v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium">Notifications</h4>
                      <p className="text-sm text-gray-500">Enable notification dispatch</p>
                    </div>
                    <Switch 
                      checked={featureFlags.notifications_enabled}
                      onCheckedChange={(v) => updateFeatureFlag("notifications_enabled", v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium">Strict Capability Enforcement</h4>
                      <p className="text-sm text-gray-500">Block agents exceeding declared capabilities</p>
                    </div>
                    <Switch 
                      checked={featureFlags.strict_capability_enforcement}
                      onCheckedChange={(v) => updateFeatureFlag("strict_capability_enforcement", v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium">Rate Limit Warnings</h4>
                      <p className="text-sm text-gray-500">Show warnings when approaching limits</p>
                    </div>
                    <Switch 
                      checked={featureFlags.rate_limit_warnings_enabled}
                      onCheckedChange={(v) => updateFeatureFlag("rate_limit_warnings_enabled", v)}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Usage Tab */}
        <TabsContent value="usage">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Agents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-bold">{rateLimits?.agents?.current || 0}</span>
                  <span className="text-gray-500">/ {rateLimits?.agents?.limit || 10}</span>
                </div>
                <Progress 
                  value={rateLimits?.agents?.percentage || 0} 
                  className={`mt-2 h-2 ${rateLimits?.agents?.percentage >= 90 ? "[&>div]:bg-red-500" : ""}`}
                />
                <p className="text-xs text-gray-400 mt-1">{rateLimits?.agents?.percentage?.toFixed(1)}% used</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Daily Executions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-bold">{rateLimits?.daily_executions?.current || 0}</span>
                  <span className="text-gray-500">/ {rateLimits?.daily_executions?.limit || 1000}</span>
                </div>
                <Progress 
                  value={rateLimits?.daily_executions?.percentage || 0} 
                  className={`mt-2 h-2 ${rateLimits?.daily_executions?.percentage >= 80 ? "[&>div]:bg-amber-500" : ""}`}
                />
                <p className="text-xs text-gray-400 mt-1">{rateLimits?.daily_executions?.percentage?.toFixed(1)}% used</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Plan</CardTitle>
              </CardHeader>
              <CardContent>
                <span className="text-2xl font-bold capitalize">{rateLimits?.plan || "Professional"}</span>
                <p className="text-xs text-gray-400 mt-2">
                  {rateLimits?.can_add_agents ? "✓ Can add agents" : "✗ Agent limit reached"}
                </p>
                <p className="text-xs text-gray-400">
                  {rateLimits?.can_execute ? "✓ Can execute" : "✗ Execution limit reached"}
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Agent Manifest</DialogTitle>
            <DialogDescription>
              Upload an agent.manifest.json file to register a new external agent
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div 
              className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <FileJson className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-sm text-gray-600">
                Click to select or drag and drop
              </p>
              <p className="text-xs text-gray-400 mt-1">
                agent.manifest.json
              </p>
            </div>
            
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept=".json"
              onChange={handleFileUpload}
            />
            
            <div className="text-xs text-gray-500 space-y-1">
              <p><strong>Required fields:</strong> agent_id, agent_name, agent_type, provider, version, webhook_url, trigger_events, inputs</p>
              <p><strong>Webhook URL:</strong> Must use HTTPS</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancel
            </Button>
            <Button disabled={uploading} onClick={() => fileInputRef.current?.click()}>
              {uploading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Select File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Dialog with Dry-Run Mode */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Test Agent: {selectedAgent?.agent_name}</DialogTitle>
            <DialogDescription>
              Send a test payload to the agent webhook
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Dry-Run Toggle */}
            <div className="flex items-center justify-between p-3 border rounded-lg bg-blue-50">
              <div className="flex items-center gap-2">
                <TestTube className="h-5 w-5 text-blue-600" />
                <div>
                  <h4 className="font-medium text-sm">Dry-Run Mode</h4>
                  <p className="text-xs text-gray-500">No state mutation, no notifications triggered</p>
                </div>
              </div>
              <Switch checked={isDryRun} onCheckedChange={setIsDryRun} />
            </div>

            {!isDryRun && (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="pt-3 pb-2">
                  <div className="flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="text-sm font-medium">Live Mode - State will be modified</span>
                  </div>
                </CardContent>
              </Card>
            )}

            <div>
              <Label>Test Payload (JSON)</Label>
              <Textarea
                value={testPayload}
                onChange={(e) => setTestPayload(e.target.value)}
                className="font-mono text-sm h-40"
                placeholder='{"key": "value"}'
              />
            </div>
            
            {testResult && (
              <Card className={testResult.status === "success" ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    {testResult.status === "success" ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-600" />
                    )}
                    {testResult.dry_run ? "Dry-Run" : "Test"} Result: {testResult.status}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm">
                  <div className="space-y-1">
                    <p><strong>Duration:</strong> {testResult.duration_ms}ms</p>
                    {testResult.dry_run && (
                      <>
                        <p><strong>State Mutated:</strong> {testResult.state_mutated ? "Yes" : "No"}</p>
                        <p><strong>Notifications:</strong> {testResult.notifications_triggered ? "Triggered" : "None"}</p>
                      </>
                    )}
                    {testResult.response_summary && (
                      <p><strong>Response:</strong> {testResult.response_summary}</p>
                    )}
                    {testResult.error_message && (
                      <p className="text-red-600"><strong>Error:</strong> {testResult.error_message}</p>
                    )}
                    {testResult.capability_warnings?.length > 0 && (
                      <div className="mt-2">
                        <p className="font-medium text-amber-700">Capability Warnings:</p>
                        {testResult.capability_warnings.map((w, i) => (
                          <p key={i} className="text-amber-600 text-xs">{w.message}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)}>
              Close
            </Button>
            <Button onClick={runTest} disabled={testing}>
              {testing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <TestTube className="h-4 w-4 mr-2" />}
              {isDryRun ? "Run Dry-Run" : "Run Live Test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog with Circuit Breaker Tab */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedAgent && getProviderIcon(selectedAgent.provider)}
              {selectedAgent?.agent_name}
            </DialogTitle>
            <DialogDescription>
              Agent ID: {selectedAgent?.agent_id} | Version: {selectedAgent?.version}
            </DialogDescription>
          </DialogHeader>
          
          {selectedAgent && (
            <Tabs defaultValue="health" className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="health">Health</TabsTrigger>
                <TabsTrigger value="config">Configuration</TabsTrigger>
                <TabsTrigger value="circuit">Circuit Breaker</TabsTrigger>
                <TabsTrigger value="executions">Executions</TabsTrigger>
              </TabsList>
              
              <TabsContent value="health" className="flex-1 overflow-auto">
                <div className="space-y-4 p-1">
                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Success Rate</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <span className={`text-3xl font-bold ${
                          selectedAgent.health?.success_rate >= 90 ? "text-green-600" :
                          selectedAgent.health?.success_rate >= 70 ? "text-amber-600" : "text-red-600"
                        }`}>
                          {selectedAgent.health?.success_rate?.toFixed(1) || 0}%
                        </span>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Avg Response Time</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <span className="text-3xl font-bold">
                          {selectedAgent.health?.avg_response_time_ms?.toFixed(0) || 0}ms
                        </span>
                      </CardContent>
                    </Card>
                  </div>
                  {selectedAgent.health?.last_error && (
                    <Card className="border-red-200 bg-red-50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-red-700">Last Error</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-red-600">{selectedAgent.health.last_error}</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="config" className="flex-1 overflow-auto">
                <div className="space-y-4 p-1">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Agent Configuration</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div><strong>Type:</strong> {selectedAgent.agent_type}</div>
                        <div><strong>Provider:</strong> {getProviderLabel(selectedAgent.provider)}</div>
                        <div><strong>Status:</strong> {selectedAgent.status}</div>
                        <div><strong>Active Version:</strong> {selectedAgent.is_active_version ? "Yes" : "No"}</div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Trigger Events</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {selectedAgent.trigger_events?.map((event) => (
                          <Badge key={event} variant="outline">{event}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Capabilities
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {selectedAgent.capabilities?.map(getCapabilityBadge)}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="circuit" className="flex-1 overflow-auto">
                <div className="space-y-4 p-1">
                  {circuitEvents && (
                    <>
                      <Card className={circuitEvents.current_status === "circuit_open" ? "border-red-200 bg-red-50" : ""}>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">Circuit Breaker Status</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">
                                Status: {getStatusBadge(circuitEvents.current_status)}
                              </p>
                              <p className="text-sm text-gray-500">Failure Count: {circuitEvents.failure_count}</p>
                              {circuitEvents.circuit_open_until && (
                                <p className="text-sm text-red-600">
                                  Opens: {new Date(circuitEvents.circuit_open_until).toLocaleString()}
                                </p>
                              )}
                            </div>
                            {circuitEvents.current_status === "circuit_open" && (
                              <Button onClick={() => resetCircuitBreaker(selectedAgent.id)}>
                                <RotateCcw className="h-4 w-4 mr-2" />
                                Reset Circuit
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>

                      {circuitEvents.events?.length > 0 && (
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Circuit Events</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {circuitEvents.events.map((e, i) => (
                                <div key={i} className="flex items-center justify-between p-2 border rounded text-sm">
                                  <div>
                                    <Badge variant={e.event_type === "circuit_open" ? "destructive" : "secondary"}>
                                      {e.event_type}
                                    </Badge>
                                    {e.reset_by && <span className="ml-2 text-gray-500">by {e.reset_by}</span>}
                                  </div>
                                  <span className="text-gray-500">{new Date(e.timestamp).toLocaleString()}</span>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {circuitEvents.recent_failures?.length > 0 && (
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Recent Failures</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {circuitEvents.recent_failures.map((f) => (
                                <div key={f.id} className="p-2 border rounded text-sm">
                                  <div className="flex items-center justify-between">
                                    <Badge variant="destructive">{f.status}</Badge>
                                    <span className="text-gray-500">{new Date(f.timestamp).toLocaleString()}</span>
                                  </div>
                                  {f.error_message && (
                                    <p className="text-red-600 text-xs mt-1 truncate">{f.error_message}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="executions" className="flex-1 overflow-auto">
                <div className="space-y-2 p-1">
                  {executions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No executions recorded yet
                    </div>
                  ) : (
                    executions.map((exec) => (
                      <div key={exec.id} className="flex items-center justify-between p-3 border rounded-lg text-sm">
                        <div className="flex items-center gap-3">
                          {exec.status === "success" ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          <div>
                            <p className="font-medium">{exec.trigger_event}</p>
                            <p className="text-xs text-gray-500">{new Date(exec.started_at).toLocaleString()}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p>{exec.duration_ms}ms</p>
                          {exec.error_message && (
                            <p className="text-xs text-red-500 max-w-xs truncate">{exec.error_message}</p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Versions Dialog */}
      <Dialog open={showVersionsDialog} onOpenChange={setShowVersionsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Version History: {selectedAgent?.agent_name}
            </DialogTitle>
            <DialogDescription>
              Manage agent versions and rollback if needed
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4 max-h-96 overflow-auto">
            {versions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No versions available
              </div>
            ) : (
              versions.map((v) => (
                <div 
                  key={v.id} 
                  className={`flex items-center justify-between p-3 border rounded-lg ${v.is_active ? "bg-green-50 border-green-200" : ""}`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">v{v.version}</Badge>
                      {v.is_active && <Badge className="bg-green-600">Active</Badge>}
                      {getStatusBadge(v.status)}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Created: {new Date(v.created_at).toLocaleString()}
                    </p>
                  </div>
                  {!v.is_active && v.status !== "disabled" && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => rollbackVersion(v.id)}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Rollback
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVersionsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
